/**
 * SUPER_ADMIN-only Users management API.
 *
 * Backed by the /settings/users router. Every endpoint requires the
 * caller to hold Role.SUPER_ADMIN; the SPA hides the entry point
 * (Settings tab) for everyone else and the server rejects with 403
 * regardless if a non-admin somehow constructs the URL directly.
 */
import client from "./client";

export type SettingsUserRole =
  | "EMPLOYEE"
  | "MANAGER"
  | "HR_ADMIN"
  | "SUPER_ADMIN";

export type SettingsUserStatus = "active" | "pending_setup" | "inactive";

export interface SettingsUserRow {
  id: string;
  employee_id: string | null;
  name: string;
  email: string;
  role: SettingsUserRole;
  status: SettingsUserStatus;
  last_login_at: string | null;
  created_at: string;
}

export interface ListUsersParams {
  search?: string;
  role?: SettingsUserRole;
  status?: SettingsUserStatus;
  sort?: "name" | "last_login" | "created";
}

export async function listSettingsUsers(
  params: ListUsersParams = {},
): Promise<SettingsUserRow[]> {
  const query: Record<string, string> = {};
  if (params.search) query.search = params.search;
  if (params.role) query.role = params.role;
  if (params.status) query.status = params.status;
  if (params.sort) query.sort = params.sort;
  const res = await client.get<SettingsUserRow[]>("/settings/users", {
    params: query,
  });
  return res.data;
}

export async function changeUserRole(
  userId: string,
  role: SettingsUserRole,
): Promise<SettingsUserRow> {
  const res = await client.put<SettingsUserRow>(
    `/settings/users/${userId}/role`,
    { role },
  );
  return res.data;
}

export async function adminResetPassword(userId: string): Promise<void> {
  await client.post(`/settings/users/${userId}/reset-password`);
}

export async function deactivateUser(
  userId: string,
): Promise<SettingsUserRow> {
  const res = await client.put<SettingsUserRow>(
    `/settings/users/${userId}/deactivate`,
  );
  return res.data;
}

export async function reactivateUser(
  userId: string,
): Promise<SettingsUserRow> {
  const res = await client.put<SettingsUserRow>(
    `/settings/users/${userId}/reactivate`,
  );
  return res.data;
}

export async function resendInvite(userId: string): Promise<void> {
  await client.post(`/settings/users/${userId}/resend-invite`);
}
